import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';

const CloseIcon = ({ size = 20, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const FeederViewModal = ({ open, onClose, feederId }) => {
  const [feederDetails, setFeederDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      const code = typeof feederId === 'string' ? feederId : (feederId?.Code);
      if (code) {
        fetchFeederDetails(code);
      }
    }
  }, [open, feederId]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const fetchFeederDetails = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/feeder/${code}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      setFeederDetails(response.data);
    } catch (error) {
      console.error('Error fetching feeder details:', error);
      setError('Failed to load feeder details');
      setFeederDetails(null);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !feederId) return null;

  const feeder = feederDetails || feederId;
  const isActive = feeder.Active === 1 || feeder.Active === true;
  const statusText = isActive ? 'Active' : 'Inactive';
  const statusStyle = isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}>
      <style>
        {`
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          .hide-scrollbar::-webkit-scrollbar { display: none; }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Header */}
        <div className="flex-shrink-0 p-8 pb-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Feeder Details</h2>
            <div className="flex gap-2">
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* General Info */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">General Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Feeder Code</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase font-semibold">{feeder.Code || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Description</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.Description || '-'}</div>
                    </div>
                     <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-[#64748b]">GST Registration No</label>
                    <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.Gst_Registration_No || '-'}</div>
                  </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Sub Station</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.SubStation || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Status</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>{statusText}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Info */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Location & Hierarchy</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">Circle</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.Circle || '-'}</div></div>
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">Division</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.Division || '-'}</div></div>
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">Sub Division</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.SubDivision || '-'}</div></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">District</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.District || '-'}</div></div>
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">City</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.City || '-'}</div></div>
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">State</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.State || '-'}</div></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">Country</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.Country || '-'}</div></div>
                    <div className="flex flex-col gap-2"><label className="text-sm font-medium text-[#64748b]">Pin Code</label><div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.PinCode || '-'}</div></div>
                  </div>
                  <div className="mb-4">
                    <label className="text-sm font-medium text-[#64748b]">Address</label>
                    <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px] uppercase">{feeder.Address || '-'}</div>
                  </div>
                 
                </div>

                {/* Relations */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Relations & Personnel</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Parent Site</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.ParentSiteName || feeder.ParentSiteCode || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Project No</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.ProjectNo || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Site Engineer</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{feeder.SiteEngineerName || feeder.SiteEngineerCode || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeederViewModal;